import React from 'react';
import { connect } from 'react-redux';
import './AgreementLanding.css';
import './Help.css';
import NewAgreementButton from './NewAgreementButton';
import usePageAnchor from './usePageAnchor';

function Help({ auth, hash }) {
  const loggedIn = auth.status === 'loggedin';
  usePageAnchor(hash);

  return (
    <main className="agreementHome helpPage">
      <header className="agreementHero helpHero">
        <div className="agreementHomeInner helpHeroLayout">
          <div>
            <div className="agreementEyebrow">Help &amp; getting started</div>
            <h1>How to use Atstake</h1>
            <p className="agreementIntro">Make your first agreement, understand the terms, and know what happens next.</p>
            <div className="agreementHomeActions">
              <a className="agreementTextLink" href="#gettingstarted">Start with the basics <span aria-hidden="true">↓</span></a>
            </div>
          </div>
          <nav className="helpContents" aria-label="On this page">
            <div className="agreementEyebrow">On this page</div>
            <a href="#gettingstarted"><span>01</span> Getting started</a>
            <a href="#beforefunding"><span>02</span> Before committing funds</a>
            <a href="#guides"><span>03</span> Guides &amp; answers</a>
          </nav>
        </div>
      </header>

      <div className="agreementHomeInner helpEssentials">
        <section id="gettingstarted" aria-labelledby="getting-started-title">
          <div className="agreementEyebrow">01 / Getting started</div>
          <h2 id="getting-started-title">Create an agreement</h2>
          <p className="helpSectionIntro">Atstake helps two people write down an agreement and choose how funds will be held and distributed. Bring the other person; work out the details together.</p>
          <ol className="helpChecklist">
            <li>
              <h3>Get your wallet ready</h3>
              <p>You’ll need an Ethereum-compatible wallet to sign in. Signing a login message is different from sending an on-chain transaction.</p>
              <a className="agreementTextLink" href="/faq#getwallet">About Ethereum wallets <span aria-hidden="true">→</span></a>
            </li>
            <li>
              <h3>Write a clear proposal</h3>
              <p>Choose “Create an agreement,” then select a contract type. Describe what each person will do, the deadlines, and how funds should be distributed for each outcome.</p>
              <p>You can send a proposal to someone who hasn’t joined Atstake yet.</p>
            </li>
            <li>
              <h3>Review it together</h3>
              <p>Check the terms with the other person. Agree on the deposits and an arbitrator before committing funds.</p>
              <a className="agreementTextLink" href="/#howitworks">See all four steps <span aria-hidden="true">→</span></a>
            </li>
          </ol>
          <div className="agreementHomeActions">
            <NewAgreementButton />
            {loggedIn && <a className="agreementTextLink" href="/contracts">Your contracts</a>}
          </div>
        </section>

        <aside id="beforefunding" className="helpBeforeFunding" aria-labelledby="before-funding-title">
          <div className="agreementEyebrow">02 / Before committing funds</div>
          <h2 id="before-funding-title">Review before depositing</h2>
          <div className="helpCheck">
            <h3>The terms</h3>
            <p>Make the outcome criteria specific. Include amounts, deadlines, and what happens if things don’t go to plan.</p>
          </div>
          <div className="helpCheck">
            <h3>The arbitrator</h3>
            <p>Choose someone both parties trust, and confirm they are willing and available to arbitrate. Review their fees and the dispute deadlines.</p>
            {loggedIn && <a className="agreementTextLink" href="/people">View people &amp; arbitrators <span aria-hidden="true">→</span></a>}
          </div>
          <div className="helpCheck">
            <h3>The transaction</h3>
            <p>Check the network, addresses, token, and amount in your wallet. Deposits and outcome reports are on-chain actions that require network fees.</p>
          </div>
          <details className="helpTestMode">
            <summary>Can I use the old test mode?</summary>
            <p>The legacy test mode uses Rinkeby, a retired Ethereum test network. It is not a working practice environment. Mainnet transactions use real funds and network fees.</p>
          </details>
        </aside>
      </div>

      <section id="guides" className="helpGuides" aria-labelledby="help-guides-title">
        <div className="agreementHomeInner">
          <div className="agreementEyebrow">03 / Guides &amp; answers</div>
          <h2 id="help-guides-title">Guides &amp; reference</h2>
          <div className="helpResourceGrid">
            <a className="helpResource" href="/#howitworks">
              <div><h3>How it works</h3><p>The full journey, from a proposal to the distribution of funds.</p></div>
              <span aria-hidden="true">↗</span>
            </a>
            <a className="helpResource" href="/examples">
              <div><h3>Example agreements</h3><p>Ideas for freelance work, shared goals, and other arrangements.</p></div>
              <span aria-hidden="true">↗</span>
            </a>
            <a className="helpResource" href="/incentives">
              <div><h3>Escrow &amp; incentives</h3><p>How deposits, reported outcomes, and dispute resolution fit together.</p></div>
              <span aria-hidden="true">↗</span>
            </a>
            <a className="helpResource" href="/faq">
              <div><h3>Frequently asked questions</h3><p>More on wallets, Ethereum, arbitrators, and using Atstake.</p></div>
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </section>

      <section className="helpContact agreementHomeInner" aria-labelledby="help-contact-title">
        <div>
          <h2 id="help-contact-title">Contact &amp; support</h2>
          <p>Read the FAQ, learn about the project, or get in touch by email.</p>
        </div>
        <div className="agreementHomeActions">
          <a className="agreementTextLink" href="/faq">Read the FAQ</a>
          <a className="agreementTextLink" href="/about">About Atstake</a>
          <a className="agreementTextLink" href="mailto:support@atstake.net">Email us</a>
        </div>
      </section>
    </main>
  );
}

export default connect(state => ({ auth: state.auth, hash: state.menu.hash }))(Help);
